/**
 * Canvas Engine SOT — Text Layout & Rendering
 *
 * Complete text engine extracted from editor.js (superset) and render-worker.ts.
 * Supports: auto-sizing, word wrapping with hyphenation, ellipsis, clip,
 * shadows, strokes, RTL text, vertical/horizontal alignment.
 */

import type { TextLayer, TextVariableStyleOverride } from '../types';
import { resolveColor } from '../utils/color';
import {
  applyVariables,
  resolveBoundBoolean,
  resolveBoundNumber,
  resolveBoundString,
  resolveBoundVariable,
} from '../variables';
import type { TextLayerPreviewOptions } from '../variable-preview';
import { resolveTextSegmentForPreview } from '../variable-preview';

// ─── Internal Types ─────────────────────────────────────────────────

interface LayoutLine {
  text: string;
  w: number;
  width?: number;
  segments?: LayoutSegment[];
  maxFontSize?: number;
  lineHeight?: number;
}

interface TextSegmentStyle {
  color: string;
  gradientEnabled?: boolean;
  gradientType?: 'linear' | 'radial';
  gradientStartColor?: string;
  gradientEndColor?: string;
  gradientAngle?: number;
  fontFamily: string;
  fontWeight: string | number;
  fontStyle: 'normal' | 'italic';
  fontSize?: number;
  fontScaleMode?: 'absolute' | 'proportional';
  fontScaleMultiplier?: number;
  minFontSize?: number;
  maxFontSize?: number;
  minFontScale?: number;
  maxFontScale?: number;
  visible?: boolean;
  strokeEnabled?: boolean;
  strokeColor?: string;
  strokeWidth?: number;
  shadowEnabled?: boolean;
  shadowColor?: string;
  shadowBlur?: number;
  shadowOffsetX?: number;
  shadowOffsetY?: number;
  shadowOpacity?: number;
}

interface LayoutSegment {
  text: string;
  w: number;
  style: TextSegmentStyle;
  variableKey?: string;
}

export interface TextLayoutResult {
  fontSize: number;
  lineHeight: number;
  lines: LayoutLine[];
  maxLineWidth: number;
  boxHeight: number;
  warnOverflow?: boolean;
}

// ─── Font Clamping ──────────────────────────────────────────────────

/**
 * Clamp a font size to a safe range.
 * Print-resolution templates (3300x2550 etc.) may need fonts up to ~960px.
 * Cap at 2000 to prevent OOM in headless Chrome.
 */
export function clampFont(value: unknown): number {
  const num = Math.round(Number(value));
  if (!Number.isFinite(num)) return 12;
  return Math.max(4, Math.min(2000, num));
}

const VARIABLE_TOKEN_RE = /\{\{([^}]+)\}\}/g;
const NEWLINE_TOKEN_RE = /^\r?\n$/;
const WHITESPACE_TOKEN_RE = /^\s+$/;

const FONT_SCALE_PERCENT_MIN = 10;
const FONT_SCALE_PERCENT_MAX = 1000;

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function clampScalePercent(value: unknown): number | undefined {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return undefined;
  return Math.max(FONT_SCALE_PERCENT_MIN, Math.min(FONT_SCALE_PERCENT_MAX, Math.round(numeric)));
}

function resolveOptionalBoundNumber(
  binding: string | null | undefined,
  vars: Record<string, string | number | null | undefined>,
  fallback: number | undefined,
  options?: { min?: number; max?: number },
): number | undefined {
  if (!binding) return fallback;
  const resolved = resolveBoundVariable(binding, vars);
  const numeric = Number(resolved);
  if (!Number.isFinite(numeric)) return fallback;
  let next = numeric;
  if (typeof options?.min === 'number' && next < options.min) next = options.min;
  if (typeof options?.max === 'number' && next > options.max) next = options.max;
  return next;
}

function resolveOptionalBoundString(
  binding: string | null | undefined,
  vars: Record<string, string | number | null | undefined>,
  fallback: string | undefined,
): string | undefined {
  if (!binding) return fallback;
  const resolved = resolveBoundVariable(binding, vars);
  if (resolved == null) return fallback;
  const normalized = String(resolved).trim();
  return normalized || fallback;
}

function resolveBoundEnum<T extends string>(
  binding: string | null | undefined,
  vars: Record<string, string | number | null | undefined>,
  fallback: T,
  allowed: readonly T[],
): T {
  const resolved = resolveOptionalBoundString(binding, vars, fallback);
  return allowed.includes(String(resolved).trim().toLowerCase() as T)
    ? String(resolved).trim().toLowerCase() as T
    : fallback;
}

function resolveTextRuntimeBindings(
  layer: TextLayer,
  vars: Record<string, string | number | null | undefined>,
): TextLayer {
  const resolvedLayer: TextLayer = { ...layer };
  const resolvedFont = resolveBoundNumber(layer.fontSizeVar, vars, layer.font ?? 12, { min: 4, max: 2000 });
  const hasExplicitMinFont = isFiniteNumber(layer.minFont);
  const resolvedMinFontVariable = resolveBoundVariable(layer.minFontSizeVar, vars);
  const hasBoundMinFont = Number.isFinite(Number(resolvedMinFontVariable));
  const hasStrictMinFont = hasExplicitMinFont || hasBoundMinFont;
  const fallbackMinFont = hasExplicitMinFont
    ? layer.minFont
    : Math.max(4, Math.round(resolvedFont * 0.4));
  const fallbackMaxFont = typeof layer.maxFont === 'number' && Number.isFinite(layer.maxFont)
    ? Math.max(layer.maxFont, resolvedFont)
    : Math.max(resolvedFont, fallbackMinFont);
  const resolvedMinFont = resolveBoundNumber(layer.minFontSizeVar, vars, fallbackMinFont, { min: 4, max: 2000 });
  const resolvedMaxFont = resolveBoundNumber(layer.maxFontSizeVar, vars, fallbackMaxFont, { min: 4, max: 2000 });

  resolvedLayer.font = resolvedFont;
  resolvedLayer.minFont = hasStrictMinFont ? resolvedMinFont : Math.min(resolvedMinFont, resolvedMaxFont);
  resolvedLayer.maxFont = Math.max(resolvedMinFont, resolvedMaxFont);

  return resolvedLayer;
}

function resolveSegmentFontSize(style: TextSegmentStyle, size: number): number {
  const baseSize = clampFont(size);
  let resolvedSize = typeof style.fontSize === 'number' && Number.isFinite(style.fontSize)
    ? clampFont(style.fontSize)
    : baseSize;

  if (style.fontScaleMode === 'proportional' && typeof style.fontScaleMultiplier === 'number' && Number.isFinite(style.fontScaleMultiplier)) {
    resolvedSize = baseSize * style.fontScaleMultiplier;
  }

  if (typeof style.minFontScale === 'number' && Number.isFinite(style.minFontScale)) {
    resolvedSize = Math.max(resolvedSize, baseSize * (style.minFontScale / 100));
  }
  if (typeof style.maxFontScale === 'number' && Number.isFinite(style.maxFontScale)) {
    resolvedSize = Math.min(resolvedSize, baseSize * (style.maxFontScale / 100));
  }
  if (typeof style.minFontSize === 'number' && Number.isFinite(style.minFontSize)) {
    resolvedSize = Math.max(resolvedSize, style.minFontSize);
  }
  if (typeof style.maxFontSize === 'number' && Number.isFinite(style.maxFontSize)) {
    resolvedSize = Math.min(resolvedSize, style.maxFontSize);
  }

  return clampFont(resolvedSize);
}

function buildFontShorthand(style: TextSegmentStyle, size: number): string {
  const resolvedSize = resolveSegmentFontSize(style, size);
  return `${style.fontStyle} ${style.fontWeight || 700} ${resolvedSize}px ${style.fontFamily}`;
}

function measureSegmentWidth(
  ctx: CanvasRenderingContext2D,
  segment: Pick<LayoutSegment, 'text' | 'style'>,
  size: number,
): number {
  ctx.save();
  ctx.font = buildFontShorthand(segment.style, size);
  const width = ctx.measureText(segment.text).width;
  ctx.restore();
  return width;
}

function getBaseTextSegmentStyle(
  layer: TextLayer,
  vars: Record<string, string | number | null | undefined>,
): TextSegmentStyle {
  const fillColor = resolveBoundString(layer.colorVar, vars, layer.color || '000000');
  const strokeColor = resolveBoundString(layer.strokeColorVar, vars, layer.strokeColor || fillColor || '000000');
  const shadowColor = resolveBoundString(layer.fxShadowColorVar, vars, layer.fxShadowColor || '000000');

  return {
    color: fillColor,
    gradientEnabled: Boolean(layer.gradientEnabled),
    gradientType: layer.gradientType === 'radial' ? 'radial' : 'linear',
    gradientStartColor: layer.gradientStartColor || fillColor || '000000',
    gradientEndColor: layer.gradientEndColor || fillColor || '000000',
    gradientAngle: typeof layer.gradientAngle === 'number' ? layer.gradientAngle : 0,
    fontFamily: layer.fontFamily,
    fontWeight: layer.fontWeight || '700',
    fontStyle: layer.fontStyle === 'italic' ? 'italic' : 'normal',
    visible: true,
    strokeEnabled: Boolean(layer.strokeEnabled),
    strokeColor,
    strokeWidth: layer.strokeWidth || 1,
    shadowEnabled: Boolean(layer.fxShadow),
    shadowColor,
    shadowBlur: layer.fxShadowBlur || 10,
    shadowOffsetX: layer.fxShadowOffsetX !== undefined ? layer.fxShadowOffsetX : 3,
    shadowOffsetY: layer.fxShadowOffsetY !== undefined ? layer.fxShadowOffsetY : 3,
    shadowOpacity: layer.fxShadowOpacity || 50,
  };
}

function mergeVariableTextSegmentStyle(
  baseStyle: TextSegmentStyle,
  override: TextVariableStyleOverride | null | undefined,
  layer: TextLayer,
  vars: Record<string, string | number | null | undefined>,
): TextSegmentStyle {
  if (!override || override.mode !== 'custom') {
    return baseStyle;
  }

  const fontScaleMode = resolveBoundEnum(
    override.fontScaleModeVar,
    vars,
    override.fontScaleMode === 'proportional' ? 'proportional' : 'absolute',
    ['absolute', 'proportional'],
  );
  const configuredFontSize = resolveOptionalBoundNumber(
    override.fontSizeVar,
    vars,
    typeof override.fontSize === 'number' && Number.isFinite(override.fontSize)
      ? clampFont(override.fontSize)
      : undefined,
    { min: 4, max: 2000 },
  );
  const referenceFontSize = clampFont(layer.font || 40);
  const fontScaleMultiplier = fontScaleMode === 'proportional' && typeof configuredFontSize === 'number'
    ? Math.max(0.1, Math.min(10, configuredFontSize / referenceFontSize))
    : undefined;
  const minFontSize = resolveOptionalBoundNumber(
    override.minFontSizeVar,
    vars,
    typeof override.minFontSize === 'number' && Number.isFinite(override.minFontSize)
      ? clampFont(override.minFontSize)
      : undefined,
    { min: 4, max: 2000 },
  );
  const maxFontSize = resolveOptionalBoundNumber(
    override.maxFontSizeVar,
    vars,
    typeof override.maxFontSize === 'number' && Number.isFinite(override.maxFontSize)
      ? clampFont(override.maxFontSize)
      : undefined,
    { min: 4, max: 2000 },
  );
  const minFontScale = clampScalePercent(resolveOptionalBoundNumber(
    override.minFontScaleVar,
    vars,
    typeof override.minFontScale === 'number' && Number.isFinite(override.minFontScale)
      ? override.minFontScale
      : undefined,
    { min: FONT_SCALE_PERCENT_MIN, max: FONT_SCALE_PERCENT_MAX },
  ));
  const maxFontScale = clampScalePercent(resolveOptionalBoundNumber(
    override.maxFontScaleVar,
    vars,
    typeof override.maxFontScale === 'number' && Number.isFinite(override.maxFontScale)
      ? override.maxFontScale
      : undefined,
    { min: FONT_SCALE_PERCENT_MIN, max: FONT_SCALE_PERCENT_MAX },
  ));
  const resolvedMinFontSize = typeof minFontSize === 'number' && typeof maxFontSize === 'number' && minFontSize > maxFontSize
    ? maxFontSize
    : minFontSize;
  const resolvedMaxFontSize = typeof minFontSize === 'number' && typeof maxFontSize === 'number' && maxFontSize < minFontSize
    ? minFontSize
    : maxFontSize;
  const resolvedMinFontScale = typeof minFontScale === 'number' && typeof maxFontScale === 'number' && minFontScale > maxFontScale
    ? maxFontScale
    : minFontScale;
  const resolvedMaxFontScale = typeof minFontScale === 'number' && typeof maxFontScale === 'number' && maxFontScale < minFontScale
    ? minFontScale
    : maxFontScale;
  const resolvedFontStyle = resolveBoundEnum(
    override.fontStyleVar,
    vars,
    override.fontStyle === 'italic' ? 'italic' : baseStyle.fontStyle,
    ['normal', 'italic'],
  );
  const resolvedFontWeight = resolveOptionalBoundString(
    override.fontWeightVar,
    vars,
    override.fontWeight != null ? String(override.fontWeight) : undefined,
  );
  const hasCustomFillOverride = (
    (typeof override.color === 'string' && override.color.trim().length > 0)
    || (typeof override.colorVar === 'string' && override.colorVar.trim().length > 0)
  );

  return {
    color: resolveBoundString(
      override.colorVar,
      vars,
      typeof override.color === 'string' && override.color.trim().length > 0
        ? override.color.replace(/^#/, '')
        : baseStyle.color,
    ),
    gradientEnabled: hasCustomFillOverride ? false : Boolean(baseStyle.gradientEnabled),
    gradientType: baseStyle.gradientType,
    gradientStartColor: baseStyle.gradientStartColor,
    gradientEndColor: baseStyle.gradientEndColor,
    gradientAngle: baseStyle.gradientAngle,
    fontFamily: resolveOptionalBoundString(
      override.fontFamilyVar,
      vars,
      typeof override.fontFamily === 'string' && override.fontFamily.trim().length > 0
        ? override.fontFamily.trim()
        : undefined,
    ) || baseStyle.fontFamily,
    fontWeight: resolvedFontWeight || baseStyle.fontWeight,
    fontStyle: resolvedFontStyle,
    fontSize: fontScaleMode === 'absolute' ? configuredFontSize ?? baseStyle.fontSize : baseStyle.fontSize,
    fontScaleMode,
    fontScaleMultiplier,
    minFontSize: resolvedMinFontSize,
    maxFontSize: resolvedMaxFontSize,
    minFontScale: resolvedMinFontScale,
    maxFontScale: resolvedMaxFontScale,
    visible: resolveBoundBoolean(
      override.visibleVar,
      vars,
      override.visible === false ? false : baseStyle.visible !== false,
    ),
    strokeEnabled: resolveBoundBoolean(
      override.strokeEnabledVar,
      vars,
      typeof override.strokeEnabled === 'boolean'
        ? override.strokeEnabled
        : Boolean(baseStyle.strokeEnabled),
    ),
    strokeColor: resolveBoundString(
      override.strokeColorVar,
      vars,
      typeof override.strokeColor === 'string' && override.strokeColor.trim().length > 0
        ? override.strokeColor.replace(/^#/, '')
        : (baseStyle.strokeColor || baseStyle.color),
    ),
    strokeWidth: resolveBoundNumber(
      override.strokeWidthVar,
      vars,
      typeof override.strokeWidth === 'number' && Number.isFinite(override.strokeWidth)
        ? override.strokeWidth
        : (baseStyle.strokeWidth || 1),
      { min: 1, max: 20 },
    ),
    shadowEnabled: resolveBoundBoolean(
      override.fxShadowVar,
      vars,
      typeof override.fxShadow === 'boolean'
        ? override.fxShadow
        : Boolean(baseStyle.shadowEnabled),
    ),
    shadowColor: resolveBoundString(
      override.fxShadowColorVar,
      vars,
      typeof override.fxShadowColor === 'string' && override.fxShadowColor.trim().length > 0
        ? override.fxShadowColor.replace(/^#/, '')
        : (baseStyle.shadowColor || '000000'),
    ),
    shadowBlur: resolveBoundNumber(
      override.fxShadowBlurVar,
      vars,
      typeof override.fxShadowBlur === 'number' && Number.isFinite(override.fxShadowBlur)
        ? override.fxShadowBlur
        : (baseStyle.shadowBlur || 10),
      { min: 0, max: 50 },
    ),
    shadowOffsetX: resolveBoundNumber(
      override.fxShadowOffsetXVar,
      vars,
      typeof override.fxShadowOffsetX === 'number' && Number.isFinite(override.fxShadowOffsetX)
        ? override.fxShadowOffsetX
        : (baseStyle.shadowOffsetX || 0),
      { min: -100, max: 100 },
    ),
    shadowOffsetY: resolveBoundNumber(
      override.fxShadowOffsetYVar,
      vars,
      typeof override.fxShadowOffsetY === 'number' && Number.isFinite(override.fxShadowOffsetY)
        ? override.fxShadowOffsetY
        : (baseStyle.shadowOffsetY || 0),
      { min: -100, max: 100 },
    ),
    shadowOpacity: resolveBoundNumber(
      override.fxShadowOpacityVar,
      vars,
      typeof override.fxShadowOpacity === 'number' && Number.isFinite(override.fxShadowOpacity)
        ? override.fxShadowOpacity
        : (baseStyle.shadowOpacity || 50),
      { min: 0, max: 100 },
    ),
  };
}

function areSegmentStylesEqual(left: TextSegmentStyle, right: TextSegmentStyle): boolean {
  return left.color === right.color
    && left.gradientEnabled === right.gradientEnabled
    && left.gradientType === right.gradientType
    && left.gradientStartColor === right.gradientStartColor
    && left.gradientEndColor === right.gradientEndColor
    && Number(left.gradientAngle || 0) === Number(right.gradientAngle || 0)
    && left.fontFamily === right.fontFamily
    && String(left.fontWeight || '') === String(right.fontWeight || '')
    && left.fontStyle === right.fontStyle
    && Number(left.fontSize || 0) === Number(right.fontSize || 0)
    && left.fontScaleMode === right.fontScaleMode
    && Number(left.fontScaleMultiplier || 0) === Number(right.fontScaleMultiplier || 0)
    && Number(left.minFontSize || 0) === Number(right.minFontSize || 0)
    && Number(left.maxFontSize || 0) === Number(right.maxFontSize || 0)
    && Number(left.minFontScale || 0) === Number(right.minFontScale || 0)
    && Number(left.maxFontScale || 0) === Number(right.maxFontScale || 0)
    && left.visible === right.visible
    && left.strokeEnabled === right.strokeEnabled
    && Number(left.strokeWidth || 0) === Number(right.strokeWidth || 0)
    && left.strokeColor === right.strokeColor
    && left.shadowEnabled === right.shadowEnabled
    && left.shadowColor === right.shadowColor
    && Number(left.shadowBlur || 0) === Number(right.shadowBlur || 0)
    && Number(left.shadowOffsetX || 0) === Number(right.shadowOffsetX || 0)
    && Number(left.shadowOffsetY || 0) === Number(right.shadowOffsetY || 0)
    && Number(left.shadowOpacity || 0) === Number(right.shadowOpacity || 0);
}

function pushLayoutSegment(
  segments: LayoutSegment[],
  text: string,
  style: TextSegmentStyle,
  variableKey?: string,
): void {
  if (!text) return;

  const previous = segments[segments.length - 1];
  if (previous && previous.variableKey === variableKey && areSegmentStylesEqual(previous.style, style)) {
    previous.text += text;
    previous.w = 0;
    return;
  }

  segments.push({ text, w: 0, style, variableKey });
}

function buildResolvedTextSegments(
  layer: TextLayer,
  rawText: string,
  vars: Record<string, string | number | null | undefined>,
  preview?: TextLayerPreviewOptions,
): LayoutSegment[] {
  const source = String(rawText ?? '');
  if (!source.length) return [];

  const baseStyle = getBaseTextSegmentStyle(layer, vars);
  const variableStyles = layer.variableStyles ?? {};
  const segments: LayoutSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  VARIABLE_TOKEN_RE.lastIndex = 0;
  while ((match = VARIABLE_TOKEN_RE.exec(source))) {
    const leadingText = source.slice(lastIndex, match.index);
    pushLayoutSegment(segments, leadingText, baseStyle);

    const variableKey = match[1].trim();
    const rawValue = Object.prototype.hasOwnProperty.call(vars, variableKey)
      ? vars[variableKey]
      : '';
    const resolvedValue = resolveTextSegmentForPreview(variableKey, rawValue, vars, preview);
    const variableStyle = mergeVariableTextSegmentStyle(baseStyle, variableStyles[variableKey], layer, vars);
    if (variableStyle.visible !== false) {
      pushLayoutSegment(segments, resolvedValue, variableStyle, variableKey);
    }
    lastIndex = match.index + match[0].length;
  }

  const trailingText = source.slice(lastIndex);
  pushLayoutSegment(segments, trailingText, baseStyle);
  return segments;
}

type StyledToken = LayoutSegment & {
  isNewline?: boolean;
  isWhitespace?: boolean;
};

function tokenizeStyledSegments(segments: LayoutSegment[]): StyledToken[] {
  const tokens: StyledToken[] = [];

  segments.forEach((segment) => {
    const parts = segment.text.split(/(\r?\n)/);
    parts.forEach((part) => {
      if (!part) return;
      if (NEWLINE_TOKEN_RE.test(part)) {
        tokens.push({ ...segment, text: part, w: 0, isNewline: true });
        return;
      }

      const chunks = part.match(/\S+\s*|\s+/g) ?? [part];
      chunks.forEach((chunk) => {
        if (!chunk) return;
        tokens.push({
          ...segment,
          text: chunk,
          w: 0,
          isWhitespace: WHITESPACE_TOKEN_RE.test(chunk),
        });
      });
    });
  });

  return tokens;
}

function breakStyledTokenToWidth(
  token: StyledToken,
  maxWidth: number,
  ctx: CanvasRenderingContext2D,
  size: number,
): LayoutSegment[] {
  const text = token.text.replace(/\s+$/u, '');
  const trailingWhitespace = token.text.slice(text.length);
  const fragments: LayoutSegment[] = [];

  if (!text.length) {
    return [];
  }

  let current = '';
  for (const character of text) {
    const next = `${current}${character}`;
    const nextWidth = measureSegmentWidth(ctx, { text: next, style: token.style }, size);
    if (current && nextWidth > maxWidth) {
      fragments.push({
        text: current,
        w: measureSegmentWidth(ctx, { text: current, style: token.style }, size),
        style: token.style,
        variableKey: token.variableKey,
      });
      current = character;
      continue;
    }
    current = next;
  }

  if (current.length) {
    const finalText = `${current}${trailingWhitespace}`;
    fragments.push({
      text: finalText,
      w: measureSegmentWidth(ctx, { text: finalText, style: token.style }, size),
      style: token.style,
      variableKey: token.variableKey,
    });
  }

  return fragments;
}

function finalizeStyledLine(segments: LayoutSegment[]): LayoutLine {
  const text = segments.map((segment) => segment.text).join('');
  const width = segments.reduce((sum, segment) => sum + segment.w, 0);
  return {
    text,
    w: width,
    width,
    segments,
  };
}

function getLineMaxFontSize(line: Pick<LayoutLine, 'segments'>, fallbackSize: number): number {
  if (!line.segments?.length) {
    return fallbackSize;
  }

  return line.segments.reduce((maxSize, segment) => {
    const nextSize = resolveSegmentFontSize(segment.style, fallbackSize);
    return Math.max(maxSize, nextSize);
  }, fallbackSize);
}

function getResolvedLineHeight(line: LayoutLine, fallbackSize: number, fallbackLineHeight?: number): number {
  if (typeof line.lineHeight === 'number' && Number.isFinite(line.lineHeight)) {
    return line.lineHeight;
  }
  if (typeof line.maxFontSize === 'number' && Number.isFinite(line.maxFontSize)) {
    return Math.round(line.maxFontSize * 1.2);
  }
  if (typeof fallbackLineHeight === 'number' && Number.isFinite(fallbackLineHeight)) {
    return fallbackLineHeight;
  }
  return Math.round(fallbackSize * 1.2);
}

function withLineMetrics(line: LayoutLine, fallbackSize: number, lineHeightMul = 1.2): LayoutLine {
  const maxFontSize = getLineMaxFontSize(line, fallbackSize);
  return {
    ...line,
    maxFontSize,
    lineHeight: Math.round(maxFontSize * lineHeightMul),
  };
}

/** Layer line-height multiplier (default 1.2 = previous hardcoded behavior). */
function resolveLineHeightMul(layer: TextLayer): number {
  const v = Number((layer as any).lineHeight);
  return Number.isFinite(v) && v > 0 ? v : 1.2;
}

/** Layer letter-spacing in px (default 0). */
function resolveLetterSpacing(layer: TextLayer): number {
  const v = Number((layer as any).letterSpacing);
  return Number.isFinite(v) ? v : 0;
}

/** Apply letter-spacing to a context if non-zero and supported (graceful no-op otherwise). */
function applyLetterSpacing(ctx: CanvasRenderingContext2D, letterSpacing: number): void {
  try {
    (ctx as any).letterSpacing = `${letterSpacing}px`;
  } catch {
    /* unsupported — ignore */
  }
}

function getTotalTextHeight(lines: LayoutLine[], fallbackSize: number, fallbackLineHeight?: number): number {
  if (!lines.length) {
    return getResolvedLineHeight({ text: '', w: 0 }, fallbackSize, fallbackLineHeight);
  }

  return lines.reduce(
    (sum, line) => sum + getResolvedLineHeight(line, fallbackSize, fallbackLineHeight),
    0,
  );
}

function wrapStyledSegmentsToWidth(
  segments: LayoutSegment[],
  maxWidth: number,
  ctx: CanvasRenderingContext2D,
  singleLineMode: boolean,
  size: number,
): LayoutLine[] {
  const tokens = tokenizeStyledSegments(segments);
  const lines: LayoutLine[] = [];
  let currentSegments: LayoutSegment[] = [];
  let currentWidth = 0;

  const pushCurrentLine = () => {
    lines.push(finalizeStyledLine(currentSegments));
    currentSegments = [];
    currentWidth = 0;
  };

  const appendSegment = (segment: LayoutSegment) => {
    currentSegments.push(segment);
    currentWidth += segment.w;
  };

  if (singleLineMode || maxWidth <= 0) {
    tokens.forEach((token) => {
      if (token.isNewline) {
        pushCurrentLine();
        return;
      }
      const width = measureSegmentWidth(ctx, token, size);
      appendSegment({
        text: token.text,
        w: width,
        style: token.style,
        variableKey: token.variableKey,
      });
    });
    pushCurrentLine();
    return lines;
  }

  tokens.forEach((token) => {
    if (token.isNewline) {
      pushCurrentLine();
      return;
    }

    if (token.isWhitespace && currentSegments.length === 0) {
      return;
    }

    const tokenWidth = measureSegmentWidth(ctx, token, size);
    if (currentSegments.length === 0 || currentWidth + tokenWidth <= maxWidth) {
      appendSegment({
        text: token.text,
        w: tokenWidth,
        style: token.style,
        variableKey: token.variableKey,
      });
      return;
    }

    if (token.isWhitespace) {
      pushCurrentLine();
      return;
    }

    if (tokenWidth > maxWidth) {
      const fragments = breakStyledTokenToWidth(token, maxWidth, ctx, size);
      fragments.forEach((fragment, index) => {
        if (currentSegments.length > 0) {
          pushCurrentLine();
        }
        appendSegment(fragment);
        if (index < fragments.length - 1) {
          pushCurrentLine();
        }
      });
      return;
    }

    pushCurrentLine();
    appendSegment({
      text: token.text,
      w: tokenWidth,
      style: token.style,
      variableKey: token.variableKey,
    });
  });

  pushCurrentLine();
  return lines;
}

// ─── Word Wrapping ──────────────────────────────────────────────────

/**
 * Break a string into lines that fit within maxWidth.
 * Supports manual newlines, word-level wrapping, and hyphenated word-breaking
 * for words wider than maxWidth (editor superset).
 */
export function wrapToWidth(
  text: string,
  maxWidth: number,
  ctx: CanvasRenderingContext2D,
  singleLineMode?: boolean,
): LayoutLine[] {
  const paras = String(text ?? '').split(/\r?\n/);
  const lines: LayoutLine[] = [];
  const measure = (s: string) => ctx.measureText(s).width;

  if (singleLineMode) {
    for (const p of paras) {
      lines.push({ text: p, w: measure(p) });
    }
    return lines;
  }

  if (!maxWidth || maxWidth <= 0) {
    for (const p of paras) {
      lines.push({ text: p, w: measure(p) });
    }
    return lines;
  }

  // Helper: break a single word that's too wide into multiple lines with hyphens
  function breakWord(word: string): string[] {
    const result: string[] = [];
    const hyphen = '-';
    const hyphenW = measure(hyphen);
    let remaining = word;

    while (remaining.length > 0) {
      if (measure(remaining) <= maxWidth) {
        result.push(remaining);
        break;
      }

      let fitLen = 0;
      for (let i = 1; i <= remaining.length; i++) {
        const chunk = remaining.slice(0, i);
        const chunkWithHyphen = chunk + hyphen;
        if (measure(chunkWithHyphen) <= maxWidth) {
          fitLen = i;
        } else {
          break;
        }
      }

      if (fitLen === 0) fitLen = 1;

      const chunk = remaining.slice(0, fitLen);
      remaining = remaining.slice(fitLen);

      if (remaining.length > 0) {
        result.push(chunk + hyphen);
      } else {
        result.push(chunk);
      }
    }

    return result;
  }

  for (const p of paras) {
    if (p === '') {
      lines.push({ text: '', w: 0 });
      continue;
    }

    const words = p.split(/\s+/);
    let cur = '';

    for (const w of words) {
      if (measure(w) > maxWidth) {
        if (cur) {
          lines.push({ text: cur, w: measure(cur) });
          cur = '';
        }
        const pieces = breakWord(w);
        for (let i = 0; i < pieces.length; i++) {
          if (i === pieces.length - 1) {
            cur = pieces[i];
          } else {
            lines.push({ text: pieces[i], w: measure(pieces[i]) });
          }
        }
        continue;
      }

      const test = cur ? cur + ' ' + w : w;
      if (measure(test) <= maxWidth || !cur) {
        cur = test;
      } else {
        lines.push({ text: cur, w: measure(cur) });
        cur = w;
      }
    }

    if (cur) {
      lines.push({ text: cur, w: measure(cur) });
    }
  }

  return lines;
}

// ─── Ellipsis ───────────────────────────────────────────────────────

/**
 * Truncate lines that overflow the box height, appending `…` to the last visible line.
 * Uses clip-aware visible line counting from editor's improved version.
 */
export function ellipsize(
  lines: LayoutLine[],
  maxWidth: number,
  boxHeight: number,
  ctx: CanvasRenderingContext2D,
  fontSize: number,
  fontFamily: string,
): void {
  ctx.save();
  ctx.font = `bold ${fontSize}px ${fontFamily}`;
  const ell = '…';

  if (boxHeight > 0) {
    const boxTop = -boxHeight / 2;
    const boxBottom = boxHeight / 2;
    const startY = boxTop + (lines[0]?.maxFontSize ?? fontSize);

    let visibleLineCount = 0;
    let drawY = startY;
    for (let i = 0; i < lines.length; i += 1) {
      const line = lines[i];
      const lineHeight = getResolvedLineHeight(line, fontSize);
      const lineTop = drawY - lineHeight * 0.8;
      const lineBottom = drawY + lineHeight * 0.25;
      if (lineTop >= boxTop && lineBottom <= boxBottom) {
        visibleLineCount++;
      } else if (lineTop >= boxTop) {
        break;
      }
      drawY += lineHeight;
    }

    const maxLines = Math.max(1, visibleLineCount);
    if (lines.length > maxLines) {
      const truncated = lines.length > maxLines;
      lines.splice(maxLines);
      const last = lines[lines.length - 1];
      if (last) {
        const original = last.text;
        let trimmed = false;
        while (ctx.measureText(last.text + ell).width > maxWidth && last.text.length) {
          last.text = last.text.slice(0, -1);
          trimmed = true;
        }
        if (truncated || trimmed || last.text !== original) {
          last.text += ell;
        }
        last.w = ctx.measureText(last.text).width;
      }
      ctx.restore();
      return;
    }
  }

  const last = lines[lines.length - 1];
  if (!last) {
    ctx.restore();
    return;
  }
  const original = last.text;
  while (ctx.measureText(last.text + ell).width > maxWidth && last.text.length) {
    last.text = last.text.slice(0, -1);
  }
  if (last.text !== original) {
    last.text += ell;
  }
  last.w = ctx.measureText(last.text).width;
  ctx.restore();
}

/**
 * Clip line-mode text to maxWidth by removing words that don't fit.
 */
export function clipLineToWidth(
  lines: LayoutLine[],
  maxWidth: number,
  ctx: CanvasRenderingContext2D,
  fontSize: number,
  fontFamily: string,
): void {
  if (!lines.length || maxWidth <= 0) return;

  ctx.save();
  ctx.font = `bold ${fontSize}px ${fontFamily}`;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (ctx.measureText(line.text).width <= maxWidth) continue;

    const words = line.text.split(/\s+/);
    let clipped = '';

    for (let j = 0; j < words.length; j++) {
      const testStr = clipped ? clipped + ' ' + words[j] : words[j];
      if (ctx.measureText(testStr).width > maxWidth) break;
      clipped = testStr;
    }

    line.text = clipped;
    line.w = ctx.measureText(clipped).width;
  }

  ctx.restore();
}

// ─── Fit Testing ────────────────────────────────────────────────────

function fitsLayout(
  layer: TextLayer,
  lines: LayoutLine[],
  lineHeight: number,
  maxWidth: number,
  boxHeight: number,
  singleLine: boolean,
  fontSize: number,
): boolean {
  if (!layer.constrain || !maxWidth) return true;
  if (singleLine) {
    return lines.length <= 1 && (!lines[0] || lines[0].w <= maxWidth);
  }
  const tooWide = lines.some(li => li.w > maxWidth);
  if (tooWide) return false;
  if (boxHeight <= 0) return true;

  if (layer.overflow === 'clip') {
    const boxTop = -boxHeight / 2;
    const boxBottom = boxHeight / 2;
    const startY = boxTop + (lines[0]?.maxFontSize ?? fontSize);
    let drawY = startY;
    let visibleCount = 0;
    for (let i = 0; i < lines.length; i += 1) {
      const currentLine = lines[i];
      const resolvedLineHeight = getResolvedLineHeight(currentLine, fontSize, lineHeight);
      const lineTop = drawY - resolvedLineHeight * 0.8;
      const lineBottom = drawY + resolvedLineHeight * 0.25;
      if (lineTop >= boxTop && lineBottom <= boxBottom) {
        visibleCount++;
      }
      drawY += resolvedLineHeight;
    }
    return visibleCount === lines.length;
  }

  return getTotalTextHeight(lines, fontSize, lineHeight) <= boxHeight;
}

// ─── Text Layout Engine ─────────────────────────────────────────────

/**
 * Core text layout engine: binary-searches font size between minFont/maxFont
 * to fit within maxWidth/boxHeight constraints. Returns layout measurements
 * for rendering.
 *
 * Merged from editor.js prepareTextLayout() and render-worker.ts prepareTextLayout().
 */
export function prepareTextLayout(
  layer: TextLayer,
  rawText: string,
  ctx: CanvasRenderingContext2D,
  forcedSize?: number | null,
  vars: Record<string, string | number | null | undefined> = {},
  preview?: TextLayerPreviewOptions,
): TextLayoutResult {
  const maxWidth = layer.constrain ? (layer.maxWidth || 0) : 0;
  const boxHeight = (layer.mode === 'box' && layer.constrain) ? (layer.boxHeight || 0) : 0;
  const maxFont = clampFont(layer.maxFont || layer.font);
  const hasExplicitMinFont = isFiniteNumber(layer.minFont);
  const minFont = clampFont(hasExplicitMinFont ? layer.minFont : Math.max(4, Math.round((layer.font || 12) * 0.4)));
  const singleLine = layer.mode === 'line';
  const useRawTemplate =
    preview?.mode === 'tokens'
    || (preview?.mode === 'depth' && (preview.depth ?? 0) <= 0);
  const resolvedText = useRawTemplate ? String(rawText ?? '') : applyVariables(rawText, vars);
  const styledSegments = buildResolvedTextSegments(layer, rawText, vars, preview);
  const baseStyle = getBaseTextSegmentStyle(layer, vars);
  const useStyledSegments = styledSegments.some((segment) => Boolean(segment.variableKey));
  const lineHeightMul = resolveLineHeightMul(layer);
  const letterSpacing = resolveLetterSpacing(layer);

  function measureAt(size: number) {
    let lines: LayoutLine[];
    if (useStyledSegments) {
      ctx.save();
      if (letterSpacing) applyLetterSpacing(ctx, letterSpacing);
      lines = wrapStyledSegmentsToWidth(styledSegments, maxWidth, ctx, singleLine, size);
      ctx.restore();
    } else {
      ctx.save();
      if (letterSpacing) applyLetterSpacing(ctx, letterSpacing);
      ctx.font = buildFontShorthand(baseStyle, size);
      lines = wrapToWidth(resolvedText, maxWidth, ctx, singleLine);
      ctx.restore();
    }
    const linesWithMetrics = lines.map((line) => withLineMetrics(line, size, lineHeightMul));
    return {
      lines: linesWithMetrics,
      size,
      lineHeight: linesWithMetrics.reduce(
        (maxHeight, line) => Math.max(maxHeight, getResolvedLineHeight(line, size)),
        Math.round(size * lineHeightMul),
      ),
    };
  }

  let chosen: number;

  if (typeof forcedSize === 'number' && forcedSize > 0) {
    chosen = clampFont(forcedSize);
  } else if (layer.constrain) {
    const lo = minFont;
    const hi = Math.max(minFont, maxFont);

    if (fitsLayout(layer, measureAt(hi).lines, Math.round(hi * 1.2), maxWidth, boxHeight, singleLine, hi)) {
      chosen = hi;
    } else if (!fitsLayout(layer, measureAt(lo).lines, Math.round(lo * 1.2), maxWidth, boxHeight, singleLine, lo)) {
      chosen = lo;
    } else {
      let best = lo, L = lo, R = hi;
      while (L <= R) {
        const mid = Math.floor((L + R) / 2);
        const m = measureAt(mid);
        if (fitsLayout(layer, m.lines, m.lineHeight, maxWidth, boxHeight, singleLine, mid)) {
          best = mid;
          L = mid + 1;
        } else {
          R = mid - 1;
        }
      }
      chosen = best;
    }
  } else {
    chosen = clampFont(layer.font);
  }

  chosen = Math.max(clampFont(chosen), minFont);

  const pack = measureAt(chosen);
  let lines = pack.lines;
  const lineHeight = pack.lineHeight;

  let warnOverflow = false;
  if (layer.constrain && !fitsLayout(layer, lines, lineHeight, maxWidth, boxHeight, singleLine, chosen)) {
    if (layer.overflow === 'ellipsis') {
      ellipsize(lines, maxWidth, boxHeight, ctx, chosen, layer.fontFamily);
    } else if (layer.overflow === 'warn') {
      warnOverflow = true;
    } else if (layer.overflow === 'clip' && singleLine && maxWidth > 0) {
      clipLineToWidth(lines, maxWidth, ctx, chosen, layer.fontFamily);
    }
  }

  lines = lines.map((line) => {
    if (!line.segments?.length) {
      return withLineMetrics(line, chosen);
    }

    const segmentText = line.segments.map((segment) => segment.text).join('');
    if (segmentText === line.text) {
      return line;
    }

    return {
      ...line,
      segments: [{ text: line.text, w: 0, style: baseStyle }],
    };
  }).map((line) => withLineMetrics(line, chosen, lineHeightMul));

  // Final measurement pass
  ctx.save();
  if (letterSpacing) applyLetterSpacing(ctx, letterSpacing);
  let maxLineWidth = 0;
  lines.forEach(line => {
    if (line.segments?.length) {
      let totalWidth = 0;
      line.segments = line.segments.map((segment) => {
        const width = measureSegmentWidth(ctx, segment, chosen);
        totalWidth += width;
        return { ...segment, w: width };
      });
      line.text = line.segments.map((segment) => segment.text).join('');
      line.w = totalWidth;
      line.width = totalWidth;
    } else {
      ctx.font = buildFontShorthand(baseStyle, chosen);
      line.w = ctx.measureText(line.text).width;
      line.width = line.w;
    }
    line.maxFontSize = getLineMaxFontSize(line, chosen);
    line.lineHeight = Math.round(line.maxFontSize * lineHeightMul);
    maxLineWidth = Math.max(maxLineWidth, line.w);
  });
  ctx.restore();

  if (layer.constrain && maxWidth > 0) {
    maxLineWidth = Math.max(maxLineWidth, maxWidth);
  }

  const totalBoxHeight = (layer.mode === 'box' && layer.constrain && boxHeight > 0)
    ? boxHeight
    : getTotalTextHeight(lines, chosen, lineHeight);

  return {
    fontSize: chosen,
    lineHeight: lines.reduce(
      (maxHeight, line) => Math.max(maxHeight, getResolvedLineHeight(line, chosen, lineHeight)),
      lineHeight,
    ),
    lines,
    maxLineWidth,
    boxHeight: totalBoxHeight,
    warnOverflow,
  };
}

function applySegmentDrawingStyles(
  ctx: CanvasRenderingContext2D,
  style: TextSegmentStyle,
  fallbackSize: number,
  gradientBounds?: { x: number; y: number; width: number; height: number },
): void {
  ctx.font = buildFontShorthand(style, fallbackSize);
  ctx.fillStyle = resolveTextFillStyle(ctx, style, gradientBounds);

  if (style.shadowEnabled) {
    const shadowOpacity = Math.max(0, Math.min(100, style.shadowOpacity || 0)) / 100;
    const shadowColorHex = String(style.shadowColor || '000000').replace(/^#/, '');
    const r = parseInt(shadowColorHex.substring(0, 2), 16) || 0;
    const g = parseInt(shadowColorHex.substring(2, 4), 16) || 0;
    const b = parseInt(shadowColorHex.substring(4, 6), 16) || 0;
    ctx.shadowColor = `rgba(${r}, ${g}, ${b}, ${shadowOpacity})`;
    ctx.shadowBlur = style.shadowBlur || 0;
    ctx.shadowOffsetX = style.shadowOffsetX || 0;
    ctx.shadowOffsetY = style.shadowOffsetY || 0;
  } else {
    ctx.shadowColor = 'rgba(0, 0, 0, 0)';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
  }
}

function createTextGradient(
  ctx: CanvasRenderingContext2D,
  style: TextSegmentStyle,
  bounds?: { x: number; y: number; width: number; height: number },
): CanvasGradient | null {
  if (!style.gradientEnabled || !bounds) {
    return null;
  }

  const width = Math.max(1, bounds.width || 0);
  const height = Math.max(1, bounds.height || 0);
  const centerX = bounds.x + width / 2;
  const centerY = bounds.y + height / 2;
  const startColor = resolveColor(style.gradientStartColor || style.color, '#000000');
  const endColor = resolveColor(style.gradientEndColor || style.color, startColor);

  if (style.gradientType === 'radial') {
    const radius = Math.max(width, height) / 2;
    const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
    gradient.addColorStop(0, startColor);
    gradient.addColorStop(1, endColor);
    return gradient;
  }

  const angle = ((style.gradientAngle ?? 0) * Math.PI) / 180;
  const length = Math.max(width, height) / 2;
  const x0 = centerX - Math.cos(angle) * length;
  const y0 = centerY - Math.sin(angle) * length;
  const x1 = centerX + Math.cos(angle) * length;
  const y1 = centerY + Math.sin(angle) * length;
  const gradient = ctx.createLinearGradient(x0, y0, x1, y1);
  gradient.addColorStop(0, startColor);
  gradient.addColorStop(1, endColor);
  return gradient;
}

function resolveTextFillStyle(
  ctx: CanvasRenderingContext2D,
  style: TextSegmentStyle,
  bounds?: { x: number; y: number; width: number; height: number },
): string | CanvasGradient {
  return createTextGradient(ctx, style, bounds) || resolveColor(style.color, '#000000');
}

// ─── Text Layer Renderer ────────────────────────────────────────────

/**
 * Full text layer renderer. Superset of both editor.js and render-worker.ts
 * text rendering: handles shadows, strokes, RTL, clip/ellipsis,
 * vertical + horizontal alignment.
 */
export function drawTextLayer(
  ctx: CanvasRenderingContext2D,
  layer: TextLayer,
  canvasWidth: number,
  canvasHeight: number,
  vars: Record<string, string | number | null | undefined>,
  preview?: TextLayerPreviewOptions,
): void {
  const resolvedLayer = resolveTextRuntimeBindings(layer, vars);
  if (!resolvedLayer.text) return;

  const cx = canvasWidth / 2 + resolvedLayer.x;
  const cy = canvasHeight / 2 + resolvedLayer.y;

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate((resolvedLayer.angle || 0) * Math.PI / 180);

  const layout = prepareTextLayout(resolvedLayer, resolvedLayer.text, ctx, null, vars, preview);
  const baseStyle = getBaseTextSegmentStyle(resolvedLayer, vars);
  const fillColor = resolveBoundString(resolvedLayer.colorVar, vars, resolvedLayer.color || '000000');
  const strokeColor = resolveBoundString(resolvedLayer.strokeColorVar, vars, resolvedLayer.strokeColor || fillColor || '000000');
  const shadowColor = resolveBoundString(resolvedLayer.fxShadowColorVar, vars, resolvedLayer.fxShadowColor || '000000');
  ctx.fillStyle = resolveColor(fillColor, '#000000');
  ctx.globalAlpha = typeof resolvedLayer.opacity === 'number' ? resolvedLayer.opacity : 1;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  const drawLetterSpacing = resolveLetterSpacing(resolvedLayer);
  if (drawLetterSpacing) applyLetterSpacing(ctx, drawLetterSpacing);

  // Apply drop shadow if enabled
  if (resolvedLayer.fxShadow) {
    const shadowOpacity = (resolvedLayer.fxShadowOpacity || 50) / 100;
    const shadowBlur = resolvedLayer.fxShadowBlur || 10;
    const shadowOffsetX = resolvedLayer.fxShadowOffsetX !== undefined ? resolvedLayer.fxShadowOffsetX : 3;
    const shadowOffsetY = resolvedLayer.fxShadowOffsetY !== undefined ? resolvedLayer.fxShadowOffsetY : 3;
    const shadowColorHex = shadowColor.replace(/^#/, '');
    const r = parseInt(shadowColorHex.substring(0, 2), 16) || 0;
    const g = parseInt(shadowColorHex.substring(2, 4), 16) || 0;
    const b = parseInt(shadowColorHex.substring(4, 6), 16) || 0;
    ctx.shadowColor = `rgba(${r}, ${g}, ${b}, ${shadowOpacity})`;
    ctx.shadowBlur = shadowBlur;
    ctx.shadowOffsetX = shadowOffsetX;
    ctx.shadowOffsetY = shadowOffsetY;
  }

  // Apply text direction (RTL/LTR)
  if (resolvedLayer.textDirection === 'rtl') {
    ctx.direction = 'rtl';
  } else if (resolvedLayer.textDirection === 'ltr') {
    ctx.direction = 'ltr';
  } else if (resolvedLayer.textDirection === 'auto') {
    // Only set if the browser supports it
    try { ctx.direction = 'inherit'; } catch { /* no-op */ }
  }

  const areaW = layout.maxLineWidth;
  const areaH = layout.boxHeight;
  const textH = getTotalTextHeight(layout.lines, layout.fontSize, layout.lineHeight);
  const gradientBounds = {
    x: -areaW / 2,
    y: -areaH / 2,
    width: areaW,
    height: areaH,
  };

  let topPad = 0;
  if (resolvedLayer.vAlign === 'middle') topPad = Math.max(0, (areaH - textH) / 2);
  if (resolvedLayer.vAlign === 'bottom') topPad = Math.max(0, areaH - textH);

  const startX = -areaW / 2;
  const startY = -areaH / 2 + topPad + (layout.lines[0]?.maxFontSize ?? layout.fontSize);

  // Clip mode: apply clipping rectangle
  const isBoxMode = resolvedLayer.mode === 'box' && (resolvedLayer.boxHeight || 0) > 0;
  const isClipMode = resolvedLayer.constrain && resolvedLayer.overflow === 'clip' && isBoxMode;
  if (isClipMode) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(-areaW / 2, -areaH / 2, areaW, areaH);
    ctx.clip();
  }

  const boxTop = -areaH / 2;
  const boxBottom = areaH / 2;

  let drawY = startY;
  layout.lines.forEach(line => {
    const currentLineHeight = getResolvedLineHeight(line, layout.fontSize, layout.lineHeight);
    // In clip mode, skip lines that don't fully fit inside the box
    if (isClipMode) {
      const lineTop = drawY - currentLineHeight * 0.8;
      const lineBottom = drawY + currentLineHeight * 0.25;
      if (lineTop < boxTop || lineBottom > boxBottom) {
        drawY += currentLineHeight;
        return;
      }
    }

    let drawX = startX;
    const isRTL = resolvedLayer.textDirection === 'rtl';
    const effectiveAlign = isRTL && !resolvedLayer.hAlign ? 'right' : (resolvedLayer.hAlign || 'left');
    if (effectiveAlign === 'center') drawX = startX + (areaW - line.w) / 2;
    if (effectiveAlign === 'right') drawX = startX + (areaW - line.w);

    if (line.segments?.length) {
      let segmentX = drawX;
      line.segments.forEach((segment) => {
        applySegmentDrawingStyles(ctx, segment.style, layout.fontSize, gradientBounds);

        if (segment.style.strokeEnabled && (segment.style.strokeWidth || 0) > 0) {
          ctx.strokeStyle = resolveColor(segment.style.strokeColor || strokeColor, '#000000');
          ctx.lineWidth = segment.style.strokeWidth || 1;
          ctx.strokeText(segment.text, segmentX, drawY);
        }

        ctx.fillText(segment.text, segmentX, drawY);
        segmentX += segment.w;
      });
    } else {
      ctx.font = buildFontShorthand(baseStyle, layout.fontSize);
      ctx.fillStyle = resolveTextFillStyle(ctx, baseStyle, gradientBounds);

      if (layer.strokeEnabled && (layer.strokeWidth || 0) > 0) {
        ctx.strokeStyle = resolveColor(strokeColor, '#000000');
        ctx.lineWidth = layer.strokeWidth || 1;
        ctx.strokeText(line.text, drawX, drawY);
      }

      ctx.fillText(line.text, drawX, drawY);
    }
    drawY += currentLineHeight;
  });

  if (isClipMode) {
    ctx.restore(); // restore from clip
  }

  ctx.restore(); // restore from translate/rotate
}
